import { Recipe } from '../types';
import { AppConfigResponse } from '../types/config';

interface ProductInfo {
  recipe: Recipe;
  price: number;
  sizeSuffix: string;
}

// Index recipes by their canonical product.id (UUID) — the key of the checkout
// distribution. Both the recipe's own productId and each size variant's productId.
function buildProductIndex(recipes: Recipe[]): Map<string, ProductInfo> {
  const index = new Map<string, ProductInfo>();
  for (const recipe of recipes) {
    index.set(recipe.productId, {
      recipe,
      price: recipe.pricePerUnit,
      sizeSuffix: '',
    });
    if (recipe.variants) {
      for (const [size, v] of Object.entries(recipe.variants)) {
        index.set(v.productId, {
          recipe,
          price: v.priceEur,
          sizeSuffix: ` ${size}`,
        });
      }
    }
  }
  return index;
}

export function clampDiscount(value: number | undefined, fallback = 0): number {
  const raw = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(0.95, Math.max(0, raw));
}

export interface PricingInput {
  initiationPackDistribution?: Record<string, number>;
  initiationPackPrice?: number;
  monthlyPlanPrice?: number;
  supplementsOneOffTotal?: number;
  recipes: Recipe[];
  config: AppConfigResponse;
  isInitiation?: boolean;
}

export interface InitiationPricing {
  hasManualInitiationPrice: boolean;
  initiationDistributionCans: number;
  hasInitiationDistribution: boolean;
  distributionInitiationOriginal: number;
  distributionInitiationPrice: number;
  hasInitiationPricingSource: boolean;
  initiationPrice: number;
  initiationOriginalPrice: number;
}

export type DiscountKind = 'initiation' | 'monthly' | 'none';

export interface CheckoutPricing extends InitiationPricing {
  backendMonthlyDiscountedPrice: number;
  backendMonthlyOriginalPrice: number;
  monthlyDiscountedPrice: number;
  supplementsOneOffTotal: number;
  todayTotal: number;
  /** Descuento efectivamente aplicado al primer pedido (0..1). */
  appliedDiscount: number;
  discountKind: DiscountKind;
  /** Monto del descuento en € (initiationOriginalPrice - initiationPrice). */
  discountAmount: number;
  initiationRecipeLines: {
    productId: string;
    name: string;
    qty: number;
    originalTotal: number;
    discountedTotal: number;
    imageUrl?: string;
    proteinType?: string;
    benefits?: string[];
  }[];
}

/**
 * Calculate initiation distribution totals from variant distribution map.
 */
function calcDistributionTotals(
  distribution: Record<string, number> | undefined,
  productIndex: Map<string, ProductInfo>,
  initiationDiscount: number,
) {
  if (!distribution) {
    return { cans: 0, original: 0, discounted: 0 };
  }
  const cans = Object.values(distribution).reduce((sum, count) => sum + (Number.isFinite(count) ? count : 0), 0);
  if (cans <= 0) {
    return { cans: 0, original: 0, discounted: 0 };
  }
  const original = Object.entries(distribution).reduce((sum, [productId, count]) => {
    const qty = Number.isFinite(count) ? count : 0;
    if (qty <= 0) return sum;
    const info = productIndex.get(productId);
    return sum + qty * (info?.price ?? 3.2);
  }, 0);
  const discounted = Number((original * (1 - initiationDiscount)).toFixed(2));
  return { cans, original, discounted };
}

/**
 * Calculate all pricing values needed for the checkout summary.
 * Usado por Step13_Checkout (resumen del carrito).
 */
export function calculateCheckoutPricing(input: PricingInput): CheckoutPricing {
  const { config, recipes } = input;
  const isInitiation = input.isInitiation !== false;
  const MONTHLY_SUBSCRIPTION_DISCOUNT = clampDiscount(config.pricing_rules.monthly_discount);
  const INITIATION_PACK_DISCOUNT = isInitiation ? clampDiscount(config.pricing_rules.initiation_discount) : 0;
  // Descuento aplicado al primer pedido (transition=iniciación 20%, full=suscripción 30%).
  const appliedDiscount = isInitiation ? INITIATION_PACK_DISCOUNT : MONTHLY_SUBSCRIPTION_DISCOUNT;

  const productIndex = buildProductIndex(recipes);

  const hasManualInitiationPrice = typeof input.initiationPackPrice === 'number' && input.initiationPackPrice > 0;

  const dist = calcDistributionTotals(input.initiationPackDistribution, productIndex, appliedDiscount);
  const hasInitiationDistribution = dist.cans > 0;
  const hasInitiationPricingSource = hasInitiationDistribution || hasManualInitiationPrice;

  const backendMonthlyDiscountedPrice =
    typeof input.monthlyPlanPrice === 'number' && Number.isFinite(input.monthlyPlanPrice) ? input.monthlyPlanPrice : 0;
  const backendMonthlyOriginalPrice =
    MONTHLY_SUBSCRIPTION_DISCOUNT < 1
      ? backendMonthlyDiscountedPrice / (1 - MONTHLY_SUBSCRIPTION_DISCOUNT)
      : backendMonthlyDiscountedPrice;
  const fallbackInitiationOriginalPrice = backendMonthlyOriginalPrice / 2;
  const fallbackInitiationPrice = Number((fallbackInitiationOriginalPrice * (1 - INITIATION_PACK_DISCOUNT)).toFixed(2));

  const initiationPrice = hasInitiationPricingSource
    ? hasInitiationDistribution
      ? dist.discounted
      : Number(input.initiationPackPrice)
    : fallbackInitiationPrice;

  // Original = precio sin descuento aplicado.
  const initiationOriginalPrice =
    appliedDiscount > 0 ? Number((initiationPrice / (1 - appliedDiscount)).toFixed(2)) : initiationPrice;

  const supplementsOneOffTotal = input.supplementsOneOffTotal ?? 0;
  const todayTotal = Number((initiationPrice + supplementsOneOffTotal).toFixed(2));

  // Build recipe line items for display
  const initiationRecipeLines =
    hasInitiationDistribution && input.initiationPackDistribution
      ? Object.entries(input.initiationPackDistribution)
          .filter(([, count]) => Number.isFinite(count) && count > 0)
          .map(([productId, count]) => {
            const info = productIndex.get(productId);
            const unitPrice = info?.price ?? 3.2;
            return {
              productId,
              name: (info?.recipe.name || productId) + (info?.sizeSuffix ?? ''),
              qty: count,
              originalTotal: Number((count * unitPrice).toFixed(2)),
              discountedTotal: Number((count * unitPrice * (1 - appliedDiscount)).toFixed(2)),
              imageUrl: info?.recipe.imageUrl,
              proteinType: info?.recipe.proteinType,
              benefits: info?.recipe.benefits,
            };
          })
      : [];

  const discountKind: DiscountKind = isInitiation
    ? INITIATION_PACK_DISCOUNT > 0
      ? 'initiation'
      : 'none'
    : MONTHLY_SUBSCRIPTION_DISCOUNT > 0
      ? 'monthly'
      : 'none';
  const discountAmount = Number((initiationOriginalPrice - initiationPrice).toFixed(2));

  return {
    hasManualInitiationPrice,
    initiationDistributionCans: dist.cans,
    hasInitiationDistribution,
    distributionInitiationOriginal: dist.original,
    distributionInitiationPrice: dist.discounted,
    hasInitiationPricingSource,
    initiationPrice,
    initiationOriginalPrice,
    backendMonthlyDiscountedPrice,
    backendMonthlyOriginalPrice,
    monthlyDiscountedPrice: backendMonthlyDiscountedPrice,
    supplementsOneOffTotal,
    todayTotal,
    appliedDiscount,
    discountKind,
    discountAmount,
    initiationRecipeLines,
  };
}
